import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldHalved, faLocationDot, faPlus, faXmark, faGlobe,
  faToggleOn, faToggleOff, faExclamationTriangle, faCheckCircle,
  faCircleInfo, faTrash, faClockRotateLeft, faRotate, faList,
  faPowerOff,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import { swalConfirm } from '../../components/shared/swalConfirm';
import {
  UiPageHeader,
  UiStatsGrid,
  UiButton,
  StatusBadge,
} from '../../components/ui';
import type { StatCardDef, StatusDef } from '../../components/ui';

const INPUT_CLASS =
  'w-full px-3.5 py-2.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';

const CARD_CLASS = 'bg-white dark:bg-slate-800 rounded-xl shadow-theme p-5 mb-5';

interface AdminIPWhitelistConfig {
  ips: string[];
  enabled: boolean;
  current_ip: string;
}

interface IPWhitelistLog {
  id: string;
  action: 'add_ip' | 'remove_ip' | 'enable' | 'disable' | 'clear_all';
  ip_address: string | null;
  changed_by_type: string;
  changed_by_name: string;
  actor_ip: string;
  before_ips: string[];
  after_ips: string[];
  created_at: string;
}

const IP_REGEX = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;

const ACTION_STATUS_MAP: Record<string, StatusDef> = {
  add_ip:    { bg: 'bg-green-100 dark:bg-green-900/30',  text: 'text-green-800 dark:text-green-400',  icon: faPlus,           label: 'เพิ่ม IP' },
  remove_ip: { bg: 'bg-red-100 dark:bg-red-900/30',    text: 'text-red-800 dark:text-red-400',    icon: faXmark,          label: 'ลบ IP' },
  enable:    { bg: 'bg-blue-100 dark:bg-blue-900/30',   text: 'text-blue-800 dark:text-blue-400',  icon: faToggleOn,       label: 'เปิดใช้งาน' },
  disable:   { bg: 'bg-gray-100 dark:bg-slate-700',     text: 'text-gray-700 dark:text-slate-300', icon: faPowerOff,       label: 'ปิดใช้งาน' },
  clear_all: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-800 dark:text-orange-400', icon: faTrash,     label: 'ล้างทั้งหมด' },
};

const AdminIPWhitelistPage: React.FC = () => {
  const [config, setConfig] = useState<AdminIPWhitelistConfig>({ ips: [], enabled: false, current_ip: '' });
  const [newIP, setNewIP] = useState('');
  const [ipError, setIPError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [logs, setLogs] = useState<IPWhitelistLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logPage, setLogPage] = useState(1);
  const [logPageSize, setLogPageSize] = useState(20);
  const [logTotal, setLogTotal] = useState(0);

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const res = await merchantApi.get('/admin/ip-whitelist');
      setConfig(res.data.data);
    } catch {
      toast.error('ไม่สามารถโหลดข้อมูลได้');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLogs = useCallback(async (page = logPage, pageSize = logPageSize) => {
    try {
      setLogsLoading(true);
      const offset = (page - 1) * pageSize;
      const res = await merchantApi.get(`/admin/ip-whitelist/logs?limit=${pageSize}&offset=${offset}`);
      setLogs(res.data.data || []);
      setLogTotal(res.data.pagination?.total || 0);
    } catch {
      console.error('Error loading logs');
    } finally {
      setLogsLoading(false);
    }
  }, [logPage, logPageSize]);

  const refreshAll = useCallback(async () => {
    await Promise.all([loadConfig(), loadLogs()]);
  }, [loadConfig, loadLogs]);

  useEffect(() => { loadConfig(); }, [loadConfig]);
  useEffect(() => { loadLogs(); }, [loadLogs]);

  const save = async (newIps: string[], newEnabled: boolean) => {
    setSaving(true);
    try {
      await merchantApi.put('/admin/ip-whitelist', { ips: newIps, enabled: newEnabled });
      setConfig((prev) => ({ ...prev, ips: newIps, enabled: newEnabled }));
      setLogPage(1);
      setTimeout(() => loadLogs(1, logPageSize), 300);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาด');
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async () => {
    const newEnabled = !config.enabled;
    let newIps = [...config.ips];
    if (newEnabled && config.current_ip && !newIps.includes(config.current_ip)) {
      newIps = [config.current_ip, ...newIps];
      toast.info(`เพิ่ม IP ของคุณ (${config.current_ip}) ลงรายการอัตโนมัติ`);
    }
    try {
      await save(newIps, newEnabled);
      toast.success(newEnabled ? 'เปิดใช้งาน IP Whitelist แล้ว' : 'ปิดใช้งาน IP Whitelist แล้ว');
    } catch {
      // handled in save
    }
  };

  const handleAddIP = async () => {
    const trimmed = newIP.trim();
    if (!trimmed) return;
    if (!IP_REGEX.test(trimmed)) {
      setIPError('รูปแบบ IP ไม่ถูกต้อง (เช่น 192.168.1.1 หรือ 10.0.0.0/24)');
      return;
    }
    if (config.ips.includes(trimmed)) {
      setIPError('IP นี้มีในรายการแล้ว');
      return;
    }
    setIPError('');
    try {
      await save([...config.ips, trimmed], config.enabled);
      toast.success('เพิ่ม IP สำเร็จ');
      setNewIP('');
    } catch {
      // handled in save
    }
  };

  const handleRemoveIP = async (ip: string) => {
    const confirmed = await swalConfirm({
      title: 'ลบ IP นี้?',
      text: `ต้องการลบ ${ip} ออกจากรายการหรือไม่`,
      icon: 'warning',
      confirmText: 'ลบ',
      confirmColor: '#ef4444',
    });
    if (!confirmed) return;
    try {
      await save(config.ips.filter((i) => i !== ip), config.enabled);
      toast.success('ลบ IP สำเร็จ');
    } catch {
      // handled in save
    }
  };

  const handleClearAll = async () => {
    const confirmed = await swalConfirm({
      title: 'ล้างรายการ IP ทั้งหมด?',
      text: 'การกระทำนี้ไม่สามารถย้อนกลับได้',
      icon: 'warning',
      confirmText: 'ล้างทั้งหมด',
      confirmColor: '#ef4444',
    });
    if (!confirmed) return;
    try {
      await save([], config.enabled);
      toast.success('ล้างรายการ IP ทั้งหมดแล้ว');
    } catch {
      // handled in save
    }
  };

  const handleUseCurrentIP = () => { setNewIP(config.current_ip); setIPError(''); };
  const handleKeyDown = (e: React.KeyboardEvent) => { if (e.key === 'Enter') handleAddIP(); };

  const isCurrentIPInList = config.ips.includes(config.current_ip);

  const statCards: StatCardDef[] = useMemo(() => [
    {
      icon: faLocationDot,
      iconClassName: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      label: 'IP ของคุณตอนนี้',
      value: (
        <code className="text-blue-600 dark:text-blue-400 font-mono text-lg">
          {config.current_ip || '—'}
        </code>
      ),
      subValue: (
        <span className={`text-xs ${isCurrentIPInList ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
          {isCurrentIPInList ? 'อยู่ในรายการ' : 'ยังไม่อยู่ในรายการ'}
        </span>
      ),
    },
    {
      icon: faShieldHalved,
      iconClassName: config.enabled
        ? 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400'
        : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-400',
      label: 'สถานะ Whitelist',
      value: (
        <span className={config.enabled ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-slate-400'}>
          {config.enabled ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
        </span>
      ),
      subValue: (
        <span className="text-xs text-gray-400 dark:text-slate-500">
          {config.enabled ? 'เฉพาะ IP ในรายการเข้าได้' : 'admin ทุก IP เข้าได้'}
        </span>
      ),
    },
    {
      icon: faList,
      iconClassName: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
      label: 'IP ในรายการ',
      value: <span className="text-purple-600 dark:text-purple-400">{config.ips.length.toLocaleString()}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">รายการที่อนุญาต</span>,
    },
    {
      icon: faClockRotateLeft,
      iconClassName: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
      label: 'ประวัติการเปลี่ยนแปลง',
      value: <span className="text-orange-600 dark:text-orange-400">{logTotal.toLocaleString()}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">รายการทั้งหมด</span>,
    },
  ], [config, isCurrentIPInList, logTotal]);

  const logColumns: Column<IPWhitelistLog>[] = useMemo(() => [
    {
      key: 'action',
      header: 'การกระทำ',
      render: (log) => <StatusBadge status={log.action} statusMap={ACTION_STATUS_MAP} />,
    },
    {
      key: 'ip_address',
      header: 'IP',
      render: (log) => log.ip_address
        ? <code className="font-mono text-sm bg-gray-100 dark:bg-slate-700 px-2 py-0.5 rounded text-gray-800 dark:text-slate-200">{log.ip_address}</code>
        : <span className="text-gray-400 dark:text-slate-500">—</span>,
    },
    {
      key: 'changed_by_name',
      header: 'โดย',
      render: (log) => (
        <span className="text-sm text-gray-700 dark:text-slate-200">{log.changed_by_name || '—'}</span>
      ),
    },
    {
      key: 'actor_ip',
      header: 'จาก IP',
      render: (log) => (
        <code className="font-mono text-xs bg-gray-100 dark:bg-slate-700 px-2 py-0.5 rounded text-gray-600 dark:text-slate-300">{log.actor_ip || '—'}</code>
      ),
    },
    {
      key: 'created_at',
      header: 'เวลา',
      render: (log) => (
        <span className="text-sm text-gray-500 dark:text-slate-400">
          {new Date(log.created_at).toLocaleString('th-TH')}
        </span>
      ),
    },
  ], []);

  return (
    <div className="w-full">
      <UiPageHeader
        icon={faShieldHalved}
        title="Admin IP Whitelist"
        subtitle="จำกัดการเข้าถึง Admin Panel เฉพาะ IP ที่อนุญาต"
        actions={
          <button
            type="button"
            onClick={() => refreshAll()}
            disabled={loading || logsLoading}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-white/20 hover:bg-white/30 disabled:opacity-50 text-white flex items-center gap-2 backdrop-blur-sm border border-white/20"
          >
            <FontAwesomeIcon icon={faRotate} className={loading || logsLoading ? 'animate-spin' : ''} />
            Refresh
          </button>
        }
      />

      <UiStatsGrid cards={statCards} loading={loading} cols={4} className="mb-6" />

      {/* Current IP quick action */}
      {!loading && config.current_ip && !isCurrentIPInList && (
        <div className={`${CARD_CLASS} flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center">
              <FontAwesomeIcon icon={faLocationDot} />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-slate-400 mb-0.5">เพิ่ม IP ปัจจุบันเข้ารายการ</p>
              <code className="text-sm font-bold text-gray-800 dark:text-slate-100">{config.current_ip}</code>
            </div>
          </div>
          <UiButton variant="secondary" icon={faPlus} onClick={handleUseCurrentIP}>
            ใส่ในช่องเพิ่ม IP
          </UiButton>
        </div>
      )}

      {/* Enable / Disable Toggle */}
      <div className={CARD_CLASS}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-800 dark:text-slate-100">เปิด/ปิด IP Whitelist</h3>
            <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">
              {config.enabled ? 'เปิดอยู่ — เฉพาะ IP ในรายการเข้าได้' : 'ปิดอยู่ — admin ทุก IP เข้าได้'}
            </p>
          </div>
          <button
            type="button"
            onClick={handleToggle}
            disabled={saving || loading}
            className={`text-4xl transition-colors disabled:opacity-50 ${config.enabled ? 'text-green-500 dark:text-green-400' : 'text-gray-300 dark:text-slate-600'}`}
          >
            <FontAwesomeIcon icon={config.enabled ? faToggleOn : faToggleOff} />
          </button>
        </div>
        {config.enabled && !isCurrentIPInList && config.current_ip && (
          <div className="mt-4 flex items-start gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-lg p-3">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-amber-500 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-700 dark:text-amber-300">
              IP ของคุณ <code className="font-mono font-bold">{config.current_ip}</code> ไม่อยู่ในรายการ
              — หาก session หมดอายุแล้ว login ใหม่จะถูกบล็อก
            </p>
          </div>
        )}
        {config.enabled && config.ips.length === 0 && (
          <div className="mt-4 flex items-start gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-lg p-3">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 dark:text-red-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-300">
              รายการ IP ว่างเปล่า — ไม่มีใครสามารถเข้า Admin Panel ได้เลย
            </p>
          </div>
        )}
      </div>

      {/* IP List */}
      <div className={`${CARD_CLASS} p-6`}>
        <div className="flex items-center gap-3 mb-5">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
            style={{ background: 'var(--theme-primary-gradient)' }}
          >
            <FontAwesomeIcon icon={faShieldHalved} />
          </div>
          <div>
            <h2 className="font-bold text-gray-800 dark:text-slate-100">รายการ IP ที่อนุญาต</h2>
            <p className="text-xs text-gray-500 dark:text-slate-400">รองรับ IPv4 และ CIDR notation (เช่น 10.0.0.0/24)</p>
          </div>
          <span className="ml-auto text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300">
            {config.ips.length} รายการ
          </span>
        </div>

        <div className="flex flex-wrap gap-2 mb-2">
          <input
            type="text"
            value={newIP}
            onChange={(e) => { setNewIP(e.target.value); setIPError(''); }}
            onKeyDown={handleKeyDown}
            placeholder="192.168.1.1 หรือ 10.0.0.0/24"
            className={`flex-1 min-w-[200px] ${INPUT_CLASS} ${ipError ? 'border-red-400 dark:border-red-500 focus:ring-red-200' : ''}`}
          />
          <UiButton variant="secondary" icon={faGlobe} onClick={handleUseCurrentIP} disabled={saving}>
            IP ปัจจุบัน
          </UiButton>
          <UiButton icon={faPlus} onClick={handleAddIP} disabled={saving || !newIP.trim()} loading={saving}>
            เพิ่ม
          </UiButton>
        </div>

        {ipError && (
          <p className="text-xs text-red-500 dark:text-red-400 mb-3 flex items-center gap-1">
            <FontAwesomeIcon icon={faCircleInfo} /> {ipError}
          </p>
        )}

        <div className="space-y-2 max-h-72 overflow-y-auto mt-4">
          {config.ips.length === 0 ? (
            <div className="text-center py-8 text-gray-400 dark:text-slate-500">
              <FontAwesomeIcon icon={faShieldHalved} className="text-4xl mb-2 block" />
              <p className="text-sm">ยังไม่มี IP ในรายการ</p>
            </div>
          ) : (
            config.ips.map((ip) => (
              <div
                key={ip}
                className={`flex items-center justify-between rounded-lg px-4 py-3 transition-colors
                  ${ip === config.current_ip
                    ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50'
                    : 'bg-gray-50 dark:bg-slate-700/50'}`}
              >
                <div className="flex items-center gap-2">
                  <code className="font-mono text-sm text-gray-800 dark:text-slate-100">{ip}</code>
                  {ip === config.current_ip && (
                    <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400 font-medium">
                      <FontAwesomeIcon icon={faCheckCircle} className="text-[10px]" />
                      IP ของคุณ
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveIP(ip)}
                  disabled={saving}
                  className="text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors disabled:opacity-40 p-1"
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
            ))
          )}
        </div>

        {config.ips.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-slate-700">
            <button
              type="button"
              onClick={handleClearAll}
              disabled={saving}
              className="flex items-center gap-2 text-sm text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors disabled:opacity-40"
            >
              <FontAwesomeIcon icon={faTrash} className="text-xs" />
              ล้างทั้งหมด
            </button>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 rounded-xl p-4 flex items-start gap-3 mb-6">
        <FontAwesomeIcon icon={faCircleInfo} className="text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
          <p className="font-semibold">หมายเหตุ</p>
          <ul className="list-disc list-inside space-y-0.5 text-blue-600 dark:text-blue-400">
            <li>ตั้งค่านี้ใช้กับ admin ทุกคนพร้อมกัน</li>
            <li>เมื่อเปิดใช้งาน ต้องมาจาก IP ในรายการเท่านั้น</li>
          </ul>
        </div>
      </div>

      {/* Audit Logs */}
      <DataTable<IPWhitelistLog>
        data={logs}
        columns={logColumns}
        loading={logsLoading}
        emptyMessage="ยังไม่มีประวัติการเปลี่ยนแปลง"
        emptyIcon={<FontAwesomeIcon icon={faClockRotateLeft} className="text-5xl text-gray-300 dark:text-slate-600" />}
        keyExtractor={(log) => log.id}
        serverPagination={{
          currentPage: logPage,
          totalItems: logTotal,
          pageSize: logPageSize,
          onPageChange: (p) => setLogPage(p),
          onPageSizeChange: (s) => { setLogPageSize(s); setLogPage(1); },
        }}
        headerContent={
          <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 flex items-center gap-2">
            <FontAwesomeIcon icon={faClockRotateLeft} className="text-orange-400" />
            ประวัติการเปลี่ยนแปลง
          </h2>
        }
        hoverable
        striped
      />
    </div>
  );
};

export default AdminIPWhitelistPage;
